
/* ============================== src/components/Modals/NoteEditorModal.jsx ============================== */
// src/components/Modals/NoteEditorModal.jsx
/**
 * Модалка редактора личной заметки. Открывается поверх TaskModal при
 * клике на плитку заметки (или на кнопку «Новая заметка»). Это вложенная
 * модалка, а не сущность уровня приложения, - поэтому она не проходит
 * через useModals/ModalRenderer, а живёт локальным состоянием TaskModal.
 *
 * useNestedModalEscape гарантирует, что Escape закроет именно этот
 * редактор, а не TaskModal, лежащий под ним: обработчик ставится в
 * capture-фазе и гасит событие до того, как оно дойдёт до bubble-обработчика
 * Modal.jsx.
 *
 * trapFocus включён: поверх TaskModal - это верхний слой, Tab не должен
 * уводить фокус в TaskModal под ним; при закрытии фокус возвращается
 * на плитку заметки (или на кнопку «Новая заметка»), по которой
 * пользователь кликнул.
 *
 * busy-флаг блокирует повторный клик «Сохранить»/«Удалить»/«Отмена»,
 * пока идёт мутация (POST/PATCH/DELETE /tasks/:id/notes через
 * useTaskNotes). До миграции на React Query store.upsertTaskNote был
 * синхронным — двойной клик был безвреден; с async-мутацией второй клик
 * создал бы вторую заметку (или второй DELETE). busy снимается в
 * finally — useTaskNotes ловит ошибки и возвращает undefined, поэтому
 * модалка не закрывается при ошибке (пользователь видит тост и может
 * повторить), а busy-флаг корректно сбрасывается.
 */
import { useState } from "react";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import { useConfirm } from "../../context/ConfirmContext";
import { DIALOGS } from "../../utils/constants";
import { useNestedModalEscape } from "../Templates/useNestedModalEscape";

export default function NoteEditorModal({ note, onSave, onDelete, onClose }) {
  useNestedModalEscape(onClose);

  const { confirm } = useConfirm();
  const isExisting = !!note?.id;

  const [title, setTitle] = useState(note?.title || "");
  const [text, setText] = useState(note?.text || "");
  const [busy, setBusy] = useState(false);

  const canSave = !!(title.trim() || text.trim());

  const handleSave = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    try {
      await onSave({
        id: note?.id,
        title: title.trim(),
        text: text.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!isExisting || busy) return;
    const ok = await confirm(DIALOGS.deleteNote);
    if (!ok) return;
    setBusy(true);
    try {
      await onDelete(note.id);
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div className="modal-foot">
      {isExisting && (
        <button
          type="button"
          className="btn danger"
          onClick={handleDelete}
          disabled={busy}
        >
          <Ic d={ICONS.trash} size={14} /> Удалить
        </button>
      )}
      <div className="spacer" />
      <button
        type="button"
        className="btn ghost"
        onClick={onClose}
        disabled={busy}
      >
        Отмена
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={!canSave || busy}
      >
        {busy ? "Сохранение…" : "Сохранить"}
      </button>
    </div>
  );

  return (
    <ModalShell
      title={isExisting ? "Заметка" : "Новая заметка"}
      onClose={onClose}
      width={560}
      footer={footer}
      showBack
      trapFocus
    >
      <div className="project-info-fields">
        <FormField label="Заголовок" value={title} onChange={setTitle} inline />
        <FormField
          label="Текст"
          type="textarea"
          rows={8}
          value={text}
          onChange={setText}
          inline
        />
      </div>
    </ModalShell>
  );
}
